// pages/admin/ReservasAdmin.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Ticket, Search, Eye, CheckCircle, XCircle, 
  Calendar, MapPin, Bus, User, Phone, Mail,
  DollarSign, Clock, RefreshCw, X, Filter, Users, IdCard, UserCheck,
  GitBranch, ChevronLeft, ChevronRight
} from 'lucide-react';
import AdminLayout from '../../layouts/AdminLayout';
import Modal from '../../components/Modal';
import ModalConfirm from '../../components/ModalConfirm';
import { showSuccess, showError, showLoading, dismissToast } from '../../utils/toast';

const API_URL = process.env.REACT_APP_API_URL;

// ✅ Helper para obtener la fecha de hoy en formato YYYY-MM-DD
const getFechaHoy = () => {
  const hoy = new Date();
  const year = hoy.getFullYear();
  const month = String(hoy.getMonth() + 1).padStart(2, '0');
  const day = String(hoy.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ReservasAdmin = () => {
  const navigate = useNavigate();
  const [reservas, setReservas] = useState([]);
  const [filteredReservas, setFilteredReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('todas');
  // ✅ Filtro de fecha inicializado con HOY
  const [filterFechaViaje, setFilterFechaViaje] = useState(getFechaHoy());
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedReserva, setSelectedReserva] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  const calendarRef = useRef(null);
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Confirmar',
    confirmButtonColor: '#e83e8c'
  });

  useEffect(() => {
    cargarReservas();
  }, []);

  useEffect(() => {
    filtrarReservas();
  }, [searchTerm, filterEstado, filterFechaViaje, reservas]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const showConfirm = (title, message, onConfirm, confirmText = 'Confirmar', confirmButtonColor = '#e83e8c') => {
    setConfirmConfig({ title, message, onConfirm, confirmText, confirmButtonColor });
    setShowConfirmModal(true);
  };

  const cargarReservas = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/reservas`);
      if (!response.ok) throw new Error('Error al cargar reservas');
      const data = await response.json();
      setReservas(data);
      setFilteredReservas(data);
    } catch (error) {
      console.error('Error:', error);
      showError('No se pudieron cargar las reservas');
    } finally {
      setLoading(false);
    }
  };

  const filtrarReservas = () => {
    let filtered = [...reservas];
    
    if (filterEstado !== 'todas') {
      filtered = filtered.filter(r => r.estado === filterEstado);
    }

    if (filterFechaViaje) {
      filtered = filtered.filter(r => {
        if (!r.servicio?.fecha) return false;
        const fechaServicio = r.servicio.fecha.split('T')[0];
        return fechaServicio === filterFechaViaje;
      });
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.codigoUnico?.toLowerCase().includes(term) ||
        r.nombrePasajero?.toLowerCase().includes(term) ||
        r.cedulaTitular?.toLowerCase().includes(term)
      );
    }
    
    setFilteredReservas(filtered);
  };

  // ✅ NUEVO: Estadísticas que respetan fecha y búsqueda, pero NO el filtro de estado
  const calcularEstadisticas = () => {
    // Aplicamos los mismos filtros que la tabla, EXCEPTO filterEstado
    let base = [...reservas];
    
    if (filterFechaViaje) {
      base = base.filter(r => {
        if (!r.servicio?.fecha) return false;
        const fechaServicio = r.servicio.fecha.split('T')[0];
        return fechaServicio === filterFechaViaje;
      });
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      base = base.filter(r => 
        r.codigoUnico?.toLowerCase().includes(term) ||
        r.nombrePasajero?.toLowerCase().includes(term) ||
        r.cedulaTitular?.toLowerCase().includes(term)
      );
    }
    
    return {
      total: base.length,
      pendientes: base.filter(r => r.estado === 'Pendiente').length,
      pagados: base.filter(r => r.estado === 'Pagado').length,
      cancelados: base.filter(r => r.estado === 'Cancelado').length
    };
  };

  const estadisticas = calcularEstadisticas();

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'Pendiente': return 'bg-yellow-100 text-yellow-800';
      case 'Pagado': return 'bg-green-100 text-green-800';
      case 'Cancelado': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'Pendiente': return <Clock className="w-4 h-4" />;
      case 'Pagado': return <CheckCircle className="w-4 h-4" />;
      case 'Cancelado': return <XCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const getOrigenDestinoReserva = (reserva) => {
    if (!reserva) return { origen: '', destino: '', esTramo: false, tramos: [] };
    
    if (reserva.tramoIds && reserva.servicio?.tramos) {
      try {
        const tramoIdsArray = reserva.tramoIds.split(',').map(Number);
        const tramosSeleccionados = reserva.servicio.tramos
          .filter(t => tramoIdsArray.includes(t.id))
          .sort((a, b) => (a.orden || 0) - (b.orden || 0));
        
        if (tramosSeleccionados.length > 0) {
          return {
            origen: tramosSeleccionados[0].origen,
            destino: tramosSeleccionados[tramosSeleccionados.length - 1].destino,
            esTramo: true,
            tramos: tramosSeleccionados,
            precioTotal: tramosSeleccionados.reduce((sum, t) => sum + t.precio, 0)
          };
        }
      } catch (e) {
        console.error('Error al parsear TramoIds:', e);
      }
    }
    
    return {
      origen: reserva.servicio?.origen || '',
      destino: reserva.servicio?.destino || '',
      esTramo: false,
      tramos: []
    };
  };

  const getHorarioTramo = (tramos) => {
    if (!tramos || tramos.length === 0) return { horaSalida: '', horaLlegada: '' };
    return {
      horaSalida: tramos[0].horaSalida?.substring(0, 5) || '',
      horaLlegada: tramos[tramos.length - 1].horaLlegada?.substring(0, 5) || ''
    };
  };

  const renderOrigenDestino = (reserva) => {
    const info = getOrigenDestinoReserva(reserva);
    
    return (
      <div>
        <div className="text-sm text-gray-800">
          {info.origen} → {info.destino}
        </div>
        {info.esTramo && (
          <div className="text-xs text-gray-400 mt-0.5">
            <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded text-[10px]">
              Tramo
            </span>
          </div>
        )}
      </div>
    );
  };

  const getMetodoPagoNombre = (metodoPago) => {
    if (!metodoPago) return 'Efectivo';
    if (metodoPago.toLowerCase() === 'transferencia') return 'Transferencia';
    return metodoPago;
  };

  const handleConfirmarPago = async (reserva) => {
    showConfirm(
      'Confirmar pago',
      `¿Confirmar pago de la reserva <strong>${reserva.codigoUnico}</strong>?<br/><br/>Total: <strong>$${formatPrecio(reserva.total)}</strong>${reserva.metodoPago === 'transferencia' ? `<br/>Comprobante: ${reserva.comprobante}` : ''}`,
      async () => {
        const toastId = showLoading('Confirmando pago...');
        
        try {
          const response = await fetch(`${API_URL}/admin/reservas/${reserva.id}/confirmar-pago`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' }
          });
          
          if (!response.ok) throw new Error('Error al confirmar pago');
          
          dismissToast(toastId);
          showSuccess('La reserva ha sido marcada como pagada');
          await cargarReservas();
        } catch (error) {
          console.error('Error:', error);
          dismissToast(toastId);
          showError('No se pudo confirmar el pago');
        }
        setShowConfirmModal(false);
      },
      'Sí, confirmar pago',
      '#22c55e'
    );
  };

  const handleCancelarReserva = async (reserva) => {
    showConfirm(
      'Cancelar reserva',
      `¿Estás seguro de cancelar la reserva <strong>${reserva.codigoUnico}</strong>?<br/><span style="color:#dc2626;font-size:12px;">Esta acción no se puede deshacer.</span>`,
      async () => {
        const toastId = showLoading('Cancelando reserva...');
        
        try {
          const response = await fetch(`${API_URL}/admin/reservas/${reserva.id}/cancelar`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' }
          });
          
          if (!response.ok) throw new Error('Error al cancelar reserva');
          
          dismissToast(toastId);
          showSuccess('La reserva ha sido cancelada correctamente');
          await cargarReservas();
        } catch (error) {
          console.error('Error:', error);
          dismissToast(toastId);
          showError('No se pudo cancelar la reserva');
        }
        setShowConfirmModal(false);
      },
      'Sí, cancelar reserva',
      '#dc2626'
    );
  };

  const verDetalles = (reserva) => {
    setSelectedReserva(reserva);
    setShowDetailModal(true);
  };

  const formatPrecio = (precio) => {
    return new Intl.NumberFormat('es-AR').format(precio);
  };

  const formatFechaCorta = (fecha) => {
    if (!fecha) return 'N/A';
    const d = new Date(fecha);
    const dia = String(d.getDate()).padStart(2, '0');
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const anio = d.getFullYear();
    const horas = String(d.getHours()).padStart(2, '0');
    const minutos = String(d.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${anio} ${horas}:${minutos}`;
  };

  const formatFechaServicioCorta = (fecha) => {
    if (!fecha) return 'N/A';
    const d = new Date(fecha);
    const dia = String(d.getDate()).padStart(2, '0');
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const anio = d.getFullYear();
    return `${dia}/${mes}/${anio}`;
  };

  const formatFechaViajeCorta = (fecha) => {
    if (!fecha) return 'N/A';
    const dateStr = fecha.split('T')[0];
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const getPasajerosAdicionales = (nombresCompletos) => {
    if (!nombresCompletos) return [];
    if (nombresCompletos === '') return [];
    
    if (typeof nombresCompletos === 'string' && !nombresCompletos.includes(',')) {
      return [];
    }
    
    if (typeof nombresCompletos === 'string') {
      const lista = nombresCompletos.split(',').map(n => n.trim()).filter(n => n !== '');
      if (lista.length > 1) {
        return lista.slice(1);
      }
      return [];
    }
    
    if (Array.isArray(nombresCompletos)) {
      if (nombresCompletos.length > 1) {
        return nombresCompletos.slice(1);
      }
      return [];
    }
    
    return [];
  };

  const getCedulasAdicionales = (cedulasCompletas) => {
    if (!cedulasCompletas) return [];
    if (cedulasCompletas === '') return [];
    
    if (typeof cedulasCompletas === 'string' && !cedulasCompletas.includes(',')) {
      return [cedulasCompletas.trim()];
    }
    
    if (typeof cedulasCompletas === 'string') {
      const lista = cedulasCompletas.split(',').map(c => c.trim()).filter(c => c !== '');
      return lista;
    }
    
    if (Array.isArray(cedulasCompletas)) {
      return cedulasCompletas;
    }
    
    return [];
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const calendarYear = calendarDate.getFullYear();
  const calendarMonth = calendarDate.getMonth();
  const daysInMonth = getDaysInMonth(calendarYear, calendarMonth);
  const firstDayOfMonth = new Date(calendarYear, calendarMonth, 1).getDay();

  const handleCalendarDateClick = (day) => {
    const yearStr = calendarYear;
    const monthStr = String(calendarMonth + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const fechaSeleccionada = `${yearStr}-${monthStr}-${dayStr}`;
    setFilterFechaViaje(fechaSeleccionada);
    setShowCalendar(false);
  };

  const getFechasConReservas = () => {
    const fechasSet = new Set();
    reservas.forEach(r => {
      if (r.servicio?.fecha) {
        fechasSet.add(r.servicio.fecha.split('T')[0]);
      }
    });
    return fechasSet;
  };

  const fechasConReservas = getFechasConReservas();

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Gestión de Reservas</h1>
          <p className="text-gray-500 text-sm">Administrá las reservas de pasajes</p>
        </div>

        {/* ✅ Estadísticas dinámicas según filtros de fecha y búsqueda */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total reservas</p>
                <p className="text-2xl font-bold text-gray-800">{estadisticas.total}</p>
              </div>
              <div className="p-3 bg-gray-100 rounded-lg">
                <Ticket className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600">Pendientes</p>
                <p className="text-2xl font-bold text-yellow-600">{estadisticas.pendientes}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#e83e8c]">Pagados</p>
                <p className="text-2xl font-bold text-[#e83e8c]">{estadisticas.pagados}</p>
              </div>
              <div className="p-3 bg-pink-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-[#e83e8c]" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Cancelados</p>
                <p className="text-2xl font-bold text-red-600">{estadisticas.cancelados}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div className="flex flex-wrap gap-3 items-center">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">Filtrar por estado:</span>
              <select
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:border-[#e83e8c] focus:ring-2 focus:ring-[#e83e8c]/20 focus:outline-none"
              >
                <option value="todas">Todas</option>
                <option value="Pendiente">Pendientes</option>
                <option value="Pagado">Pagados</option>
                <option value="Cancelado">Cancelados</option>
              </select>

              {/* ✅ Filtro por fecha de viaje (agrupado con su X) */}
              <span className="text-sm text-gray-600 ml-2">Fecha de viaje:</span>
              <div className="relative flex items-center gap-1" ref={calendarRef}>
                <button
                  onClick={() => setShowCalendar(!showCalendar)}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm border rounded-lg transition-colors ${
                    filterFechaViaje 
                      ? 'bg-[#e83e8c] text-white border-[#e83e8c]' 
                      : 'text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  {filterFechaViaje 
                    ? formatFechaViajeCorta(filterFechaViaje) 
                    : 'Todas'}
                  <ChevronRight className={`w-4 h-4 transition-transform ${showCalendar ? 'rotate-90' : ''}`} />
                </button>

                {filterFechaViaje && (
                  <button
                    onClick={() => setFilterFechaViaje('')}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Limpiar filtro de fecha"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}

                {showCalendar && (
                  <div className="absolute top-full left-0 mt-2 z-20 bg-white rounded-xl shadow-lg border border-gray-200 p-4 w-80">
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={() => setCalendarDate(new Date(calendarYear, calendarMonth - 1, 1))}
                        className="p-1 hover:bg-gray-100 rounded-lg"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <span className="font-medium text-gray-800 capitalize">
                        {calendarDate.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}
                      </span>
                      <button
                        onClick={() => setCalendarDate(new Date(calendarYear, calendarMonth + 1, 1))}
                        className="p-1 hover:bg-gray-100 rounded-lg"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-500 mb-2">
                      {['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'].map(day => (
                        <div key={day} className="py-1">{day}</div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-7 gap-1">
                      {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                        <div key={`empty-${i}`} className="h-9" />
                      ))}
                      {Array.from({ length: daysInMonth }).map((_, i) => {
                        const day = i + 1;
                        const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const tieneReservas = fechasConReservas.has(dateStr);
                        const esSeleccionada = filterFechaViaje === dateStr;
                        
                        return (
                          <button
                            key={day}
                            onClick={() => handleCalendarDateClick(day)}
                            className={`
                              h-9 rounded-lg flex items-center justify-center text-sm transition-all
                              ${esSeleccionada ? 'bg-[#e83e8c] text-white' : 'hover:bg-gray-100'}
                              ${tieneReservas && !esSeleccionada ? 'bg-pink-50 text-pink-700 font-medium' : ''}
                            `}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => {
                          const today = new Date();
                          const yearStr = today.getFullYear();
                          const monthStr = String(today.getMonth() + 1).padStart(2, '0');
                          const dayStr = String(today.getDate()).padStart(2, '0');
                          setFilterFechaViaje(`${yearStr}-${monthStr}-${dayStr}`);
                          setShowCalendar(false);
                        }}
                        className="w-full px-3 py-1.5 text-sm text-[#e83e8c] hover:bg-pink-50 rounded-lg transition-colors"
                      >
                        Ir a hoy
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* ✅ Buscador más chico */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar ticket, titular o cédula..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm w-64 focus:border-[#e83e8c] focus:ring-2 focus:ring-[#e83e8c]/20 focus:outline-none"
              />
            </div>
            
            <button
              onClick={cargarReservas}
              className="flex items-center gap-2 px-4 py-2 text-[#e83e8c] hover:bg-pink-50 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </button>
          </div>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e83e8c] mx-auto"></div>
            <p className="text-gray-500 mt-3">Cargando reservas...</p>
          </div>
        ) : filteredReservas.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Ticket className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No hay reservas que coincidan con los filtros</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Ticket</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Fecha de compra</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Fecha de viaje</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Titular</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Origen - Destino</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Pas.</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Estado</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredReservas.map((reserva) => (
                    <tr key={reserva.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs font-medium text-gray-800">{reserva.codigoUnico}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {formatFechaCorta(reserva.fechaReserva)}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="flex items-center gap-1 text-[#e83e8c] font-medium">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatFechaViajeCorta(reserva.servicio?.fecha)}
                        </div>
                        {reserva.servicio?.horaSalida && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {reserva.servicio.horaSalida.substring(0, 5)} hs
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-800">{reserva.nombrePasajero}</div>
                        <div className="text-xs text-gray-500">
                          {reserva.cedulaTitular || 'Sin cédula'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {renderOrigenDestino(reserva)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800 text-center">
                        {reserva.cantidadPasajeros || 1}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-800">
                        ${formatPrecio(reserva.total)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(reserva.estado)}`}>
                          {getEstadoIcon(reserva.estado)}
                          {reserva.estado}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => verDetalles(reserva)}
                            className="p-1.5 text-gray-500 hover:text-[#e83e8c] hover:bg-pink-50 rounded-lg transition-colors"
                            title="Ver detalles"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {reserva.estado === 'Pendiente' && (
                            <>
                              <button
                                onClick={() => handleConfirmarPago(reserva)}
                                className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                                title="Confirmar pago"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleCancelarReserva(reserva)}
                                className="p-1.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Cancelar reserva"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal de detalles */}
      {showDetailModal && selectedReserva && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full my-8">
            <div className="p-5 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white rounded-t-2xl">
              <h3 className="text-lg font-semibold text-gray-800">Detalles de la reserva</h3>
              <button onClick={() => setShowDetailModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="bg-[#e83e8c]/10 rounded-xl p-4 text-center border border-[#e83e8c]/20">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Número de ticket</p>
                <p className="text-2xl font-bold font-mono text-[#e83e8c]">{selectedReserva.codigoUnico}</p>
              </div>

              <div className="flex justify-center">
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getEstadoColor(selectedReserva.estado)}`}>
                  {getEstadoIcon(selectedReserva.estado)}
                  {selectedReserva.estado}
                </span>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Bus className="w-4 h-4 text-[#e83e8c]" />
                  Datos del viaje
                </h4>
                
                {(() => {
                  const info = getOrigenDestinoReserva(selectedReserva);
                  const metodoPagoDisplay = getMetodoPagoNombre(selectedReserva.metodoPago);
                  
                  const horarioTramo = getHorarioTramo(info.tramos);
                  
                  let horaSalidaDisplay = selectedReserva.servicio?.horaSalida?.substring(0,5) || '';
                  let horaLlegadaDisplay = selectedReserva.servicio?.horaLlegada?.substring(0,5) || '';
                  
                  if (info.esTramo && info.tramos.length > 0) {
                    if (horarioTramo.horaSalida) {
                      horaSalidaDisplay = horarioTramo.horaSalida;
                    }
                    if (horarioTramo.horaLlegada) {
                      horaLlegadaDisplay = horarioTramo.horaLlegada;
                    }
                  }
                  
                  return (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Recorrido:</span>
                        <span className="font-medium">
                          {selectedReserva.servicio?.origen} → {selectedReserva.servicio?.destino}
                        </span>
                      </div>
                      
                      {info.esTramo && info.tramos.length > 0 && (
                        <div className="flex justify-between text-sm mt-1">
                          <span className="text-gray-600">Tramo:</span>
                          <span className="font-medium text-blue-600">
                            {info.origen} → {info.destino}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex justify-between text-sm mt-2">
                        <span className="text-gray-600">Fecha:</span>
                        <span className="font-medium">{formatFechaServicioCorta(selectedReserva.servicio?.fecha)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Horario:</span>
                        <span className="font-medium">
                          Sale: {horaSalidaDisplay} - Llega: {horaLlegadaDisplay}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="bg-pink-50 rounded-xl p-4 border border-pink-100">
                <h4 className="font-semibold text-[#e83e8c] mb-3 flex items-center gap-2">
                  <UserCheck className="w-4 h-4" />
                  Datos del titular
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Nombre:</span>
                    <span className="font-medium text-gray-800">{selectedReserva.nombrePasajero}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cédula:</span>
                    <span className="font-medium text-gray-800">{selectedReserva.cedulaTitular || 'No registrada'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Teléfono WhatsApp:</span>
                    <span className="font-medium text-gray-800">{selectedReserva.telefonoPasajero}</span>
                  </div>
                  {selectedReserva.telefonoParticular && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Teléfono particular:</span>
                      <span className="font-medium text-gray-800">{selectedReserva.telefonoParticular}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedReserva.cantidadPasajeros > 1 && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#e83e8c]" />
                    Pasajeros adicionales ({selectedReserva.cantidadPasajeros - 1})
                  </h4>
                  {(() => {
                    const nombresAdicionales = getPasajerosAdicionales(selectedReserva.nombresPasajeros);
                    const cedulasAdicionales = getCedulasAdicionales(selectedReserva.cedulasPasajeros);
                    
                    if (nombresAdicionales.length === 0) {
                      return <p className="text-sm text-gray-500">No hay pasajeros adicionales registrados</p>;
                    }
                    
                    return (
                      <ul className="space-y-2">
                        {nombresAdicionales.map((nombre, index) => (
                          <li key={index} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700">{nombre}</span>
                            </div>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              Cédula: {cedulasAdicionales[index] || 'No registrada'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>
              )}

              <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Información de pago
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Método de pago:</span>
                    <span className="font-medium">
                      {getMetodoPagoNombre(selectedReserva.metodoPago)}
                    </span>
                  </div>
                  {selectedReserva.metodoPago === 'Transferencia' && selectedReserva.comprobante && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">N° de comprobante:</span>
                      <span className="font-medium font-mono">{selectedReserva.comprobante}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-[#1a1a2e] text-white rounded-xl p-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Total</span>
                  <span className="text-2xl font-bold text-[#e83e8c]">${formatPrecio(selectedReserva.total)}</span>
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setShowDetailModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cerrar
              </button>
              {selectedReserva.estado === 'Pendiente' && (
                <>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      handleConfirmarPago(selectedReserva);
                    }}
                    className="flex-1 px-4 py-2 bg-[#e83e8c] hover:bg-[#c2185b] text-white rounded-lg flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Confirmar pago
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      handleCancelarReserva(selectedReserva);
                    }}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Cancelar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación */}
      <ModalConfirm
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        confirmButtonColor={confirmConfig.confirmButtonColor}
      />
    </AdminLayout>
  );
};

export default ReservasAdmin;